import { useEffect, useState } from "react";
import { ScrollText, Download, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { PageHeader, Loading } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

const CATEGORIES = ["all", "access", "error", "deployment", "admin", "tunnel", "startup", "app"];
const LEVELS = ["all", "info", "warn", "error"];
const CAT_COLOR = {
  deployment: "text-violet-400", admin: "text-sky-400", error: "text-rose-400",
  startup: "text-emerald-400", app: "text-amber-400", access: "text-zinc-400", tunnel: "text-teal-400",
};
const LVL_COLOR = { info: "text-zinc-500", warn: "text-amber-400", error: "text-rose-400" };

export default function Logs() {
  const [logs, setLogs] = useState(null);
  const [category, setCategory] = useState("all");
  const [level, setLevel] = useState("all");
  const [q, setQ] = useState("");
  const { canWrite } = useAuth();

  const load = () => api.get("/logs", { params: { category, level, q: q || undefined } }).then(setLogs).catch(() => setLogs([]));
  useEffect(() => { load(); }, [category, level]);

  return (
    <div data-testid="logs-page">
      <PageHeader
        title="Logs"
        breadcrumb="cannon / logs"
        subtitle="access · error · deployment · admin · tunnel · startup · app · retention 14d"
        actions={
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="logs-refresh" className="rounded-md border border-zinc-800 bg-zinc-900 p-2 hover:bg-zinc-800"><RefreshCw className="h-4 w-4" /></button>
            <a href={`${API}/logs/download`} className="inline-flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm hover:bg-zinc-800"><Download className="h-4 w-4" /> Download</a>
            {canWrite && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button data-testid="logs-clear" className="inline-flex items-center gap-2 rounded-md border border-rose-500/30 bg-rose-500/5 text-rose-300 px-3 py-1.5 text-sm hover:bg-rose-500/10"><Trash2 className="h-4 w-4" /> Archive & Clear</button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-[#0c0c0e] border-zinc-800">
                  <AlertDialogHeader><AlertDialogTitle>Clear all logs?</AlertDialogTitle><AlertDialogDescription>This archives and removes all log entries.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel className="bg-zinc-900 border-zinc-800">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={async () => { await api.post("/logs/clear"); toast.success("Cleared"); load(); }} className="bg-rose-600 hover:bg-rose-500">Clear</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        }
      />

      <div className="flex items-center gap-2 mb-4">
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-36 bg-zinc-900 border-zinc-800" data-testid="log-category"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0c0c0e] border-zinc-800">{CATEGORIES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={level} onValueChange={setLevel}>
          <SelectTrigger className="w-32 bg-zinc-900 border-zinc-800" data-testid="log-level"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0c0c0e] border-zinc-800">{LEVELS.map((l) => <SelectItem key={l} value={l} className="capitalize">{l}</SelectItem>)}</SelectContent>
        </Select>
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Search messages…" data-testid="log-search" className="bg-zinc-900 border-zinc-800 max-w-xs" />
      </div>

      {!logs ? <Loading /> : (
        <div className="border border-zinc-800 rounded-lg bg-black overflow-hidden">
          <div className="max-h-[70vh] overflow-auto p-3 font-mono text-xs space-y-0.5" data-testid="logs-viewer">
            {logs.length === 0 ? <div className="text-zinc-600 text-center py-8">no log entries</div> :
              logs.map((l, i) => (
                <div key={i} className="flex gap-2 hover:bg-zinc-900/50 px-1 py-0.5 rounded">
                  <span className="text-zinc-600 shrink-0">{new Date(l.timestamp).toLocaleString([], { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>
                  <span className={cn("shrink-0 w-24 uppercase", CAT_COLOR[l.category] || "text-zinc-400")}>{l.category}</span>
                  <span className={cn("shrink-0 w-12 uppercase", LVL_COLOR[l.level] || "text-zinc-500")}>{l.level}</span>
                  <span className="text-zinc-300">{l.message}</span>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
